import { AsyncPipe } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, ViewChild, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextPipe, I18NextService } from 'angular-i18next';
import { isNil } from 'lodash';
import { ButtonComponent, FormGroupComponent, ModalComponent, ModalFooterDirective, RadioComponent } from 'mbs-ui-kit';
import { LicensesPricesData } from 'mbs-ui-shared@components/buy-license-modal/models/calculator';
import { LicensesPipesModule } from 'mbs-ui-shared@components/licenses/pipes/licenses-pipes.module';
import { LicensesSignalFacade } from 'mbs-ui-shared@facades/licenses-signal.facade';
import { LicensesFacade } from 'mbs-ui-shared@facades/licenses.facade';
import { LicenseType } from 'mbs-ui-shared@models';
import { LicensesService } from 'mbs-ui-shared@services/licenses.service';
import { objectToParams } from 'mbs-ui-shared@utils/object-to-params';
import { catchError, finalize, forkJoin, of, take } from 'rxjs';

type ServiceLicenseOption = {
  id: LicenseType;
  name: string;
  price: number | null;
};

@UntilDestroy()
@Component({
  selector: 'mbs-support-license-modal',
  templateUrl: './add-service-license.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    ReactiveFormsModule,
    ButtonComponent,
    FormGroupComponent,
    I18NextPipe,
    LicensesPipesModule,
    ModalComponent,
    ModalFooterDirective,
    RadioComponent
  ]
})
export class AddServiceLicenseComponent implements AfterViewInit {
  private licensesService = inject(LicensesService);
  private licensesFacade = inject(LicensesFacade);
  private licensesSignalFacade = inject(LicensesSignalFacade);
  private i18 = inject(I18NextService);

  @ViewChild(ModalComponent, { static: true }) baseModal: ModalComponent;

  public readonly loading = signal(false);
  public readonly optionsLoading = signal(true);
  public readonly options = signal<Array<ServiceLicenseOption>>([]);

  public readonly selectedLicenseType = new FormControl<LicenseType>(null);
  public readonly selectedValue = toSignal(this.selectedLicenseType.valueChanges, { initialValue: null });

  public readonly billingPeriod = signal<'month' | 'year'>('month');
  public readonly periodLabelKey = computed(() =>
    this.billingPeriod() === 'year' ? 'shared:licenses.licenseModal.perYear' : 'shared:licenses.licenseModal.perMonth'
  );

  ngAfterViewInit(): void {
    this.loadOptions();
  }

  private loadOptions(): void {
    const isMonthly = this.licensesSignalFacade.isMonthlyLicenseMode();

    this.billingPeriod.set(isMonthly ? 'month' : 'year');

    forkJoin({
      types: this.licensesService.getPPLicensesTypes(objectToParams({ Kind: this.baseModal.data.kind })).pipe(catchError(() => of(null))),
      prices: this.licensesFacade.getPrices().pipe(catchError(() => of(null)))
    })
      .pipe(
        take(1),
        finalize(() => this.optionsLoading.set(false)),
        untilDestroyed(this)
      )
      .subscribe(({ types, prices }) => {
        this.options.set(
          (types?.data ?? []).map((type) => ({
            id: type.id,
            name: type.name,
            price: this.getUnitPrice(prices, type.id, isMonthly)
          }))
        );

        const preselectType = this.baseModal.data.preselect;

        if (!isNil(preselectType)) this.selectedLicenseType.setValue(preselectType);
      });
  }

  private getUnitPrice(data: LicensesPricesData, licenseType: LicenseType, isMonthly: boolean): number | null {
    const priceRow = data?.licenseTypesPricesMap?.[licenseType];
    const prices = isMonthly ? priceRow?.monthlyPrices : priceRow?.annualPrices;

    return prices?.unitDiscountedPrice ?? prices?.unitListPrice ?? null;
  }

  public assign() {
    this.loading.set(true);

    const params = {
      licenseTypeId: this.selectedLicenseType.value,
      ...(this.selectedLicenseType.value === LicenseType.RestoreToCloud ? { paid: true } : {})
    };
    this.licensesService
      .licenseAssign(params)
      .pipe(
        take(1),
        catchError(() => of(false)),
        finalize(() => this.loading.set(false)),
        untilDestroyed(this)
      )
      .subscribe(() => {
        this.baseModal.save();
      });
  }

  public getLinkText(isPremiumSupport = true) {
    return this.i18.t('shared:licenses.licenseModal:' + (isPremiumSupport ? 'premiumLink' : 'supportLink'));
  }

  public getLink(isPremiumSupport = true) {
    return isPremiumSupport
      ? 'https://www.msp360.com/download/MSP360-customer-support-reference-guide.pdf'
      : 'https://www.msp360.com/download/whitepapers/onboarding-mbs.pdf';
  }
}
